import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';

export type RoleSignataire =
  | 'APPRENANT'
  | 'REPRESENTANT_LEGAL'
  | 'ENTREPRISE'
  | 'TUTEUR'
  | 'CFA'
  | 'FORMATEUR'
  | 'RESPONSABLE_PEDAGOGIQUE';

export type StatutSignature = 'EN_ATTENTE' | 'PARTIELLE' | 'SIGNEE' | 'ANNULEE';

export interface SignataireAffiche {
  id: string;
  role: RoleSignataire;
  roleLibelle: string;
  nom: string;
  email: string | null;
  signeLe: string | null;
  empreinteSignature: string | null;
}

export interface DemandeSignature {
  id: string;
  libelle: string;
  statut: StatutSignature;
  apprenantId: string | null;
  apprenant: string | null;
  documentId: string | null;
  typeDocument: string | null;
  siteCode: string;
  empreinteDocument: string | null;
  creeLe: string;
  scelleLe: string | null;
  signataires: SignataireAffiche[];
  avancement: string;
  signesCount: number;
  total: number;
}

export interface ReponseSignatures {
  enAttente: DemandeSignature[];
  signees: DemandeSignature[];
  compteurs: {
    enAttente: number;
    partielles: number;
    signees: number;
    signatairesAttendus: number;
  };
  prestataire: { nom: string; niveau: string; operationnel: boolean };
  roles: { valeur: RoleSignataire; libelle: string }[];
}

/**
 * Demandes de signature, filtrées par antenne et, depuis que la signature se
 * traite dans le dossier, par dossier. Sans `apprenantId`, le comportement
 * d'avant est conservé : tout ce que le périmètre autorise.
 */
export function useSignatures(site: string, apprenantId?: string) {
  const parametres = new URLSearchParams({ site });
  if (apprenantId) parametres.set('apprenantId', apprenantId);

  return useQuery({
    queryKey: ['signatures', site, apprenantId ?? ''],
    queryFn: () => appelApi<ReponseSignatures>(`/signatures?${parametres.toString()}`),
  });
}

/**
 * Une demande de signature se crée depuis deux écrans : le module de signature
 * et le suivi documentaire du pôle pédagogique. Les deux vues portent la même
 * donnée — les oublier ici laisserait la seconde afficher « non demandé » sur un
 * document qu'on vient de faire signer.
 */
function invalider(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: ['signatures'] });
  void client.invalidateQueries({ queryKey: ['documents-pedagogiques'] });
  // La dernière signature d'une fiche d'accompagnement clôt son suivi.
  void client.invalidateQueries({ queryKey: ['accompagnements'] });
}

export function useCreerDemandeSignature() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (entree: {
      libelle: string;
      apprenantId?: string;
      documentId?: string;
      site?: string;
      signataires: { role: RoleSignataire; nom: string; email?: string }[];
    }) => appelApi<DemandeSignature>('/signatures', { methode: 'POST', corps: entree }),
    onSuccess: () => invalider(client),
  });
}

export interface ResultatSignature {
  demande: DemandeSignature;
  mention: string;
  niveau: string;
  scellee: boolean;
}

export function useSigner() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { demandeId: string; signataireId: string; trace: string }) =>
      appelApi<ResultatSignature>(
        `/signatures/${variables.demandeId}/signataires/${variables.signataireId}`,
        { methode: 'POST', corps: { trace: variables.trace, consentement: true } },
      ),
    onSuccess: () => invalider(client),
  });
}

export interface Preuve {
  demande: string;
  statut: StatutSignature;
  empreinteDocument: string | null;
  scelleLe: string | null;
  prestataire: string;
  niveau: string;
  signataires: {
    id: string;
    role: string;
    nom: string;
    signeLe: string | null;
    empreinteSignature: string | null;
    adresseIp: string | null;
  }[];
  mention: string;
}

export function usePreuve(demandeId: string | null) {
  return useQuery({
    queryKey: ['preuve', demandeId],
    queryFn: () => appelApi<Preuve>(`/signatures/${demandeId}/preuve`),
    enabled: Boolean(demandeId),
  });
}
